import { action } from "@ember/object"
import { inject } from "@ember/service"
import Component from "@glimmer/component"
import type Session from "cc-frontend/services/session"
import Cookies from "js-cookie"
import { merge } from "lodash"
import styles from "./sign-in-with-google.module.css"

interface Args {
  stateMap?: Record<string, any>
  purpose: "GOOGLE_CLASSROOM" | "GOOGLE_AUTH"
  callback?: (data: any) => void
}

export default class SignInWithGoogleComponent extends Component<Args> {
  readonly styles = styles
  windowObjectReference: Window | null = null
  previousUrl: string | null = null

  @inject declare session: Session

  constructor(owner, args) {
    super(owner, args)
    window.addEventListener("message", this.receiveMessage, false)
  }

  willDestroy(): void {
    window.removeEventListener("message", this.receiveMessage)
  }

  // Source:
  // https://dev.to/dinkydani21/how-we-use-a-popup-for-google-and-outlook-oauth-oci

  @action
  receiveMessage(event) {
    // Do we trust the sender of this message? (might be
    // different from what we originally opened, for example).
    // if (event.origin !== BASE_URL) {
    //   return
    // }
    const { data } = event
    // if we trust the sender and the source is our popup
    if (data.source === "CC_OAUTH_FLOW") {
      this.args.callback?.(data)
    }
  }

  @action
  openSignInWindow() {
    let state = merge(
      {
        userId: this.session.userId,
        sessionId: this.session.id,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      this.args.stateMap || {}
    )

    let scopes

    if (this.args.purpose === "GOOGLE_AUTH") {
      state = merge(state, { purpose: "SIGN_IN_UP" })
      scopes = ["openid", "profile", "email"]
    }
    if (this.args.purpose === "GOOGLE_CLASSROOM") {
      state = merge(state, { purpose: "GOOGLE_CLASSROOM_AUTH" })

      scopes = [
        "openid",
        "profile",
        "email",
        "https://www.googleapis.com/auth/drive.file",
        "https://www.googleapis.com/auth/classroom.announcements",
        "https://www.googleapis.com/auth/classroom.courses.readonly",
        "https://www.googleapis.com/auth/classroom.coursework.me",
        "https://www.googleapis.com/auth/classroom.coursework.students",
        "https://www.googleapis.com/auth/classroom.courseworkmaterials",
        "https://www.googleapis.com/auth/classroom.push-notifications",
        "https://www.googleapis.com/auth/classroom.rosters.readonly",
        "https://www.googleapis.com/auth/classroom.topics",
      ]
    }

    // We can't set the state using the state so we have to use a cookie. See
    // https://github.com/achedeuzot/ueberauth_auth0/issues/168#issuecomment-877396335
    Cookies.set("cc_auth_state", btoa(JSON.stringify(state)))

    let url = `/auth/google?scope=${encodeURI(scopes.join(" "))}`
    let name = "Sign In With Google"

    // window features
    const strWindowFeatures = "toolbar=no, menubar=no, width=600, height=700, top=100, left=100"

    if (this.windowObjectReference === null || this.windowObjectReference.closed) {
      /* if the pointer to the window object in memory does not exist
      or if such pointer exists but the window was closed */
      this.windowObjectReference = window.open(url, name, strWindowFeatures)
    } else if (this.previousUrl !== url) {
      /* if the resource to load is different,
      then we load it in the already opened secondary window and then
      we bring such window back on top/in front of its parent window. */
      this.windowObjectReference = window.open(url, name, strWindowFeatures)
      this.windowObjectReference?.focus()
    } else {
      /* else the window reference must exist and the window
      is not closed; therefore, we can bring it back on top of any other
      window with the focus() method. There would be no need to re-create
      the window or to reload the referenced resource. */
      this.windowObjectReference.focus()
    }

    // add the listener for receiving a message from the popup

    // assign the previous URL
    this.previousUrl = url
  }
}
